import styled from "@emotion/styled";
import { PublicKey } from "@solana/web3.js";
import {
  darkGrey4,
  deformatNumber,
  getFormattedNumber,
  lightGrey3,
  StepNumberInput,
  transparentWhite,
  white,
} from "@stepfinance/react";
import type { FC, MouseEvent, ReactNode } from "react";
import { useRef } from "react";

import { useMarkets } from "../../contexts/market";
import { useAccountByMint, useCalculatePriceForDisplayCurrency } from "../../hooks";
import { getTokenName } from "../../utils/tokenInfo";
import { MaxHalfButtons } from "../MaxHalfButtons/MaxHalfButtons";
import { SwapTokenSelector } from "../SwapAmountInput/SwapTokenSelector";
import { UserMintBalance } from "../UserMintBalance/UserMintBalance";

export type AmountInputCustomDisable = {
  balance: boolean;
  input: boolean;
};

export type AmountInputProps = {
  id?: string;
  tokenInfoDisplay?: ReactNode;
  amount?: string;
  // maxAmount and priceOverride can be used as fallback in case searching those fields by mint doesn't work.
  mint: PublicKey | string | string[];
  maxAmount?: number;
  disabled?: boolean | AmountInputCustomDisable;
  isSwap?: boolean;
  className?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  onFocus?: () => void;
  onAmountChange?: (event: React.ChangeEvent<HTMLInputElement>, amount: string) => void;
  onMintChange?: (mint: PublicKey) => void;
  isCurrencySelectionDisabled?: boolean;
  disabledMintFromTokenList?: PublicKey;
  showMaxButton?: boolean;
  showHalfButton?: boolean;
  // Used when sending NFT's, because their price cannot be determined via mint
  // lookup in the same manner as other Tokens.
  priceOverride?: number | null;
  headerText?: React.ReactNode;
  showBalance?: boolean;
  displayMinimizedVersion?: boolean;
  data?: any;
  onlyAllowIntegers?: boolean;
  mintDecimals?: number;
};

export const AmountInput: FC<AmountInputProps> = ({
  id,
  tokenInfoDisplay,
  amount,
  mint,
  inputRef,
  onFocus,
  maxAmount,
  disabled = false,
  onAmountChange,
  onMintChange,
  isCurrencySelectionDisabled,
  disabledMintFromTokenList,
  className,
  showMaxButton = true,
  showHalfButton = false,
  priceOverride,
  headerText,
  showBalance = true,
  displayMinimizedVersion = false,
  onlyAllowIntegers = false,
  mintDecimals,
}) => {
  const { priceInUSD } = useMarkets();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const rawAmount = amount ? deformatNumber(amount) : amount;
  const rawMintAddress = mint instanceof PublicKey ? mint.toBase58() : mint;
  const price = priceOverride ? priceOverride : Array.isArray(rawMintAddress) ? 1 : priceInUSD(rawMintAddress);
  const account = useAccountByMint(Array.isArray(mint) ? undefined : mint);
  const symbol = Array.isArray(rawMintAddress)
    ? rawMintAddress.map((mint) => getTokenName(mint)).join("/")
    : getTokenName(rawMintAddress);
  const ref = useRef<HTMLInputElement>(null);
  const value = price !== null ? price * Number(rawAmount) : null;
  const { balance: balanceDisabled, input: inputDisabled } =
    typeof disabled === "boolean"
      ? {
          balance: disabled,
          input: disabled,
        }
      : disabled;

  const onMaxHalfButtonClicked = (_event: MouseEvent<HTMLElement, globalThis.MouseEvent>, newAmount: number) => {
    const balanceMultiplier = symbol === "SOL" ? 0.98 : 1.0;
    if (!balanceDisabled) {
      const activeRef = inputRef ? inputRef : ref;
      if (activeRef && activeRef.current) {
        const inputValueProperty = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value");
        const inputValue = newAmount * balanceMultiplier;
        if (
          inputValueProperty &&
          inputValueProperty.set &&
          // user can't spam if he tries clicking on the same button multiple times
          deformatNumber(activeRef.current.value) !== inputValue
        ) {
          const nativeInputValueSetter = inputValueProperty.set;
          const splitInputValue = inputValue.toString().split(".");
          nativeInputValueSetter.call(
            activeRef.current,
            getFormattedNumber(inputValue, splitInputValue[1] ? splitInputValue[1].length : 0, onlyAllowIntegers),
          );

          const inputEvent = new Event("input", { bubbles: true });
          // trigger on change event on stepinput
          activeRef.current.dispatchEvent(inputEvent);
        }
        activeRef.current.focus();
      }
    }
  };

  return (
    <>
      <HeaderDiv>
        <div className="amount-header">{headerText}</div>
        {showBalance && (
          <BalanceInfoDiv>
            {/* We only care about mint if it's a single mint(token) */}
            <UserMintBalance balance={maxAmount} mint={Array.isArray(mint) ? undefined : mint} />
            <MaxHalfButtons
              balance={maxAmount ? maxAmount : account ? account.tokenAmount.uiAmount : 0}
              onClick={onMaxHalfButtonClicked}
              disabled={balanceDisabled}
              showHalfButton={showHalfButton}
              showMaxButton={showMaxButton}
            />
          </BalanceInfoDiv>
        )}
      </HeaderDiv>
      <NumberInputDiv
        displayMinimizedVersion={displayMinimizedVersion}
        inputDisabled={inputDisabled}
        className={className}
      >
        {tokenInfoDisplay ?? (
          <SwapTokenSelector
            mintAddress={rawMintAddress}
            symbol={symbol}
            dependentMint={disabledMintFromTokenList}
            onMintChange={onMintChange}
            disabled={Array.isArray(rawMintAddress) || isCurrencySelectionDisabled}
          />
        )}
        <TopAndBottomDisplayDiv>
          <AmountAndEstimationContainer>
            <StepNumberInput
              variant="outlined"
              inputSize="small"
              id={id}
              borderless
              onlyAllowIntegers={onlyAllowIntegers}
              displayMinimizedVersion={displayMinimizedVersion}
              value={amount}
              onFocus={onFocus}
              disabled={inputDisabled}
              style={{ fontSize: "18px", height: "28px" }}
              ref={inputRef ? inputRef : ref}
              onChange={(event, value) => {
                onAmountChange?.(event, value);
              }}
              maxDecimalsAllowed={mintDecimals}
            />
            {displayMinimizedVersion || !rawAmount ? null : (
              <EstimatedPriceSpan>{calculatePriceForDisplayCurrency(value)}</EstimatedPriceSpan>
            )}
          </AmountAndEstimationContainer>
        </TopAndBottomDisplayDiv>
      </NumberInputDiv>
    </>
  );
};

export const BalanceInfoDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const EstimatedPriceSpan = styled.span`
  height: 20px;
  font-family: Space Mono;
  font-size: 12px;
  line-height: 22px;
  color: ${transparentWhite};
`;

export const HeaderDiv = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 20px;
  margin-bottom: 5px;
  .amount-header {
    font-size: 14px;
    line-height: 22px;
    color: ${white};
    margin-bottom: 16px;
  }
`;

const TopAndBottomDisplayDiv = styled.div`
  width: 100%;
`;

const NumberInputDiv = styled.div<{ inputDisabled: boolean; displayMinimizedVersion: boolean }>`
  width: 100%;
  ${(props) => (props.displayMinimizedVersion ? "" : "height: 64px;")}
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding: ${(props) => (props.displayMinimizedVersion ? "8px 12px" : "12px")};
  border-radius: ${(props) => (props.displayMinimizedVersion ? "2px" : "8px")};
  background-color: ${({ inputDisabled }) => (inputDisabled ? lightGrey3 : "black")};
  ${(props) => (props.displayMinimizedVersion ? `border: 1px solid ${darkGrey4};` : "")}
  ${({ inputDisabled }) => (inputDisabled ? "border: 1px solid rgba(120, 120, 120, 0.15);" : "")}
  .token-text {
    color: ${({ inputDisabled }) => (inputDisabled ? `${transparentWhite} !important` : white)};
  }
`;

const AmountAndEstimationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
